import React, { useCallback, useEffect, useRef, useState } from "react";
import { Collapse } from "antd";
import { change, Field, formValueSelector } from "redux-form";
import { useDispatch, useSelector } from "react-redux";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import { ContentState, EditorState, convertToRaw } from "draft-js";

import { customInput } from "../../../../../../../../common/custom-input";
import emailEditor from "../../../../../../../../common/EmailEditor";
import { CustomSwitch } from "../../../../../../../../common/customSwitch";
import styles from "../../../../../../JobStyle.module.css";
import v2Styles from "../../../../../../../v2Styles.module.css";
import emergencyStyles from "../../JobSwmsV3EmergencyProceduresContainer.module.css";
import planStyles from "../../../emergency-plans/JobSwmsV3EmergencyPlansContainer.module.css";

const { Panel } = Collapse;
const FORM_NAME = "JobSwmsV3EmergencyProceduresContainerForm";
const formSelector = formValueSelector(FORM_NAME);

const getStatusColor = (status) => {
  switch (`${status || "DRAFT"}`.toUpperCase()) {
    case "APPROVED":
      return { bg: "#D1FAE5", color: "#065F46", text: "Approved" };
    case "PENDING_APPROVAL":
      return { bg: "#DBEAFE", color: "#1E40AF", text: "Pending Approval" };
    case "REJECTED":
      return { bg: "#FEE2E2", color: "#991B1B", text: "Rejected" };
    case "DRAFT":
    default:
      return { bg: "#FEF3C7", color: "#92400E", text: "Draft" };
  }
};

const getProjectDetails = (swmsData = {}) => (
  swmsData
  && swmsData.sections
  && swmsData.sections.document_project_details
  && swmsData.sections.document_project_details.data
    ? swmsData.sections.document_project_details.data
    : {}
);

function ProcedureApplicationFields({ procedure, procedureIndex, loading }) {
  const dispatch = useDispatch();
  const procedureApplication = procedure
    && procedure.sections
    && procedure.sections.procedure_application
    && procedure.sections.procedure_application.data
    ? procedure.sections.procedure_application.data
    : {};
  const scenarioHtml = procedureApplication.scenario && procedureApplication.scenario.html
    ? procedureApplication.scenario.html
    : "";
  const objectiveHtml = procedureApplication.objective && procedureApplication.objective.html
    ? procedureApplication.objective.html
    : "";
  const formScenarioHtml = useSelector((state) => formSelector(
    state,
    `procedures[${procedureIndex}].sections.procedure_application.data.scenario.html`
  ));
  const formObjectiveHtml = useSelector((state) => formSelector(
    state,
    `procedures[${procedureIndex}].sections.procedure_application.data.objective.html`
  ));
  const resolvedScenarioHtml = formScenarioHtml !== undefined ? formScenarioHtml : scenarioHtml;
  const resolvedObjectiveHtml = formObjectiveHtml !== undefined ? formObjectiveHtml : objectiveHtml;
  const formToggle = useSelector((state) => formSelector(
    state,
    `procedures[${procedureIndex}].sections.procedure_application.data.toggle`
  ));
  const isIncluded = Number(
    formToggle === undefined ? procedureApplication.toggle : formToggle
  ) !== 0;
  const [scenarioEditorState, setScenarioEditorState] = useState(() => EditorState.createEmpty());
  const [objectiveEditorState, setObjectiveEditorState] = useState(() => EditorState.createEmpty());
  const lastScenarioHtmlRef = useRef("");
  const lastObjectiveHtmlRef = useRef("");

  const getEditorStateFromHtml = useCallback((html = "") => {
    const blocksFromHtml = htmlToDraft(html);
    if (blocksFromHtml && blocksFromHtml.contentBlocks) {
      return EditorState.createWithContent(
        ContentState.createFromBlockArray(blocksFromHtml.contentBlocks)
      );
    }
    return EditorState.createEmpty();
  }, []);

  useEffect(() => {
    if (resolvedScenarioHtml !== lastScenarioHtmlRef.current) {
      lastScenarioHtmlRef.current = resolvedScenarioHtml;
      setScenarioEditorState(getEditorStateFromHtml(resolvedScenarioHtml));
    }
  }, [getEditorStateFromHtml, resolvedScenarioHtml]);

  useEffect(() => {
    if (resolvedObjectiveHtml !== lastObjectiveHtmlRef.current) {
      lastObjectiveHtmlRef.current = resolvedObjectiveHtml;
      setObjectiveEditorState(getEditorStateFromHtml(resolvedObjectiveHtml));
    }
  }, [getEditorStateFromHtml, resolvedObjectiveHtml]);

  const updateRichText = (fieldName, setEditorState, lastHtmlRef) => (newState) => {
    setEditorState(newState);
    const html = draftToHtml(convertToRaw(newState.getCurrentContent()));
    lastHtmlRef.current = html;
    dispatch(change(FORM_NAME, `procedures[${procedureIndex}].sections.procedure_application.data.${fieldName}.html`, html));
  };

  return (
    <>
      <div className="row">
        <div className="col-12">
          <div className="d-flex align-items-end mb-1" style={{ gap: "12px" }}>
            <div className={`${styles.Label} mb-0 mr-auto`} style={{ fontSize: "14px" }}>Procedure Application</div>
            <div className="d-flex align-items-center" style={{ gap: "6px" }}>
              <span className="swms-info-toggle-hint">Included in document</span>
              <Field
                name={`procedures[${procedureIndex}].sections.procedure_application.data.toggle`}
                size="small"
                className="cs-switch-v3"
                component={CustomSwitch}
                disabled={loading}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="row mt-2">
        <div className="col-6">
          <div className={styles.Label}>Scenario</div>
          <div className="swmsv2-custom-draft-editor">
            <Field
              name={`procedures[${procedureIndex}].sections.procedure_application.data.scenario.html`}
              component={emailEditor}
              editorState={scenarioEditorState}
              onEditorStateChange={updateRichText("scenario", setScenarioEditorState, lastScenarioHtmlRef)}
              disabled={loading || !isIncluded}
            />
          </div>
        </div>
        <div className="col-6">
          <div className={styles.Label}>Objective</div>
          <div className="swmsv2-custom-draft-editor">
            <Field
              name={`procedures[${procedureIndex}].sections.procedure_application.data.objective.html`}
              component={emailEditor}
              editorState={objectiveEditorState}
              onEditorStateChange={updateRichText("objective", setObjectiveEditorState, lastObjectiveHtmlRef)}
              disabled={loading || !isIncluded}
            />
          </div>
        </div>
      </div>
    </>
  );
}

export default function JobSwmsV3EmergencyProcedureDetailsSection({
  procedure = {},
  procedureIndex,
  swmsData = {},
  status = "DRAFT",
  loading
}) {
  const projectDetails = getProjectDetails(swmsData);
  const linkedPlans = Array.isArray(procedure.linked_documents) ? procedure.linked_documents : [];
  const statusStyle = getStatusColor(status);
  const qliAreas = projectDetails.qli_areas || {};
  const areas = projectDetails.areas || {};
  const showQliAreas = Number(qliAreas.toggle) === 1;
  const showAreas = Number(areas.toggle) === 1;
  const linkedPlanNumbers = linkedPlans
    .map((plan) => plan.document_number || plan.plan_id)
    .filter(Boolean)
    .join(", ");
  const renderValueChips = (values = []) => (
    <div className={`${planStyles.ValueChipContainer} d-flex`}>
      {(Array.isArray(values) ? values : []).map((item, index) => (
        <span className={planStyles.ValueChip} key={typeof item === "string" ? item : (item.id || item.name || index)}>
          {typeof item === "string" ? item : item.name}
        </span>
      ))}
    </div>
  );

  return (
      <Panel
        key="procedure-details"
        className="custom-job-swmsv3-collapse-panel"
        header={(
          <div
            className={`${styles.BackgroundSectionHeading} custom-section-v2-panel-heading`}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer"
            }}
          >
            <span aria-hidden="true" className={emergencyStyles.ProcedureFixedSectionIcon}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" width="14" height="14">
                <path
                  fill="currentColor"
                  d="M256 160L256 224L384 224L384 160C384 124.7 355.3 96 320 96C284.7 96 256 124.7 256 160zM192 224L192 160C192 89.3 249.3 32 320 32C390.7 32 448 89.3 448 160L448 224C483.3 224 512 252.7 512 288L512 512C512 547.3 483.3 576 448 576L192 576C156.7 576 128 547.3 128 512L128 288C128 252.7 156.7 224 192 224z"
                />
              </svg>
            </span>
            Procedure Details
          </div>
        )}
      >
        <div className="container-fluid my-2 pb-2">
          <div className="row">
            <div className="col-6">
              <div className={styles.Label}>Procedure Name</div>
              <Field
                name={`procedures[${procedureIndex}].title`}
                className={v2Styles.TextField}
                component={customInput}
                disabled={loading}
              />
            </div>
            <div className="col-6">
              <div className={styles.Label}>Procedure ID</div>
              <input
                className={v2Styles.TextField}
                value={procedure.document_number || ""}
                disabled
                readOnly
              />
            </div>
          </div>
          <div className="row mt-2">
            <div className="col-3">
              <div className={styles.Label}>Version</div>
              <input
                className={v2Styles.TextField}
                value={projectDetails.swms_version || ""}
                disabled
                readOnly
              />
            </div>
            <div className="col-3">
              <div className={styles.Label}>Status</div>
              <div className="d-flex align-items-center pt-1" style={{ gap: "10px" }}>
                <div
                  className="swms-info-status-badge"
                  style={{ backgroundColor: statusStyle.bg, color: statusStyle.color }}
                >
                  {statusStyle.text}
                </div>
                <div className="swms-info-auto-updated-label">Auto-updated by workflow</div>
              </div>
            </div>
          </div>

          <hr className="swms-info-section-divider" />

          <ProcedureApplicationFields
            procedure={procedure}
            procedureIndex={procedureIndex}
            loading={loading}
          />

          <hr className="swms-info-section-divider" />

          <div className="row">
            <div className="col-12">
              <div className={styles.Label} style={{ fontSize: "14px" }}>Linked Documents</div>
            </div>
          </div>
          <div className="row mt-2">
            <div className="col-6">
              <div className={styles.Label}>Linked Emergency Plan</div>
              <input
                className={v2Styles.TextField}
                value={linkedPlanNumbers}
                placeholder="No linked Plans"
                disabled
                readOnly
              />
            </div>
            <div className="col-6">
              <div className={styles.Label}>Linked SWMS</div>
              <input
                className={v2Styles.TextField}
                value={projectDetails.swms_number || ""}
                placeholder="No linked SWMS"
                disabled
                readOnly
              />
            </div>
          </div>

          <hr className="swms-info-section-divider" />

          <div className="row">
            <div className="col-12">
              <div className={styles.Label} style={{ fontSize: "14px" }}>Work Context — from SWMS</div>
            </div>
          </div>
          <div className="row mt-2">
            <div className="col-6">
              <div className={styles.Label}>SWMS Name</div>
              <input className={v2Styles.TextField} value={projectDetails.swms_name || ""} disabled readOnly />
            </div>
            <div className="col-6">
              <div className={styles.Label}>Site Name</div>
              <input className={v2Styles.TextField} value={projectDetails.site_name || ""} disabled readOnly />
            </div>
          </div>
          <div className="row mt-2">
            <div className="col-12">
              <div className={styles.Label}>Site Address</div>
              <input className={v2Styles.TextField} value={projectDetails.site_address || ""} disabled readOnly />
            </div>
          </div>
          {projectDetails.scope_of_work && Number(projectDetails.scope_of_work.toggle) === 1 ? (
            <div className="row mt-2">
              <div className="col-12">
                <div className={styles.Label}>Scope of Work</div>
                <input
                  className={v2Styles.TextField}
                  value={projectDetails.scope_of_work.value || ""}
                  disabled
                  readOnly
                />
              </div>
            </div>
          ) : null}
          {showQliAreas || showAreas ? (
            <div className="row mt-2">
              {showQliAreas ? (
                <div className="col-6">
                  <div className={styles.Label}>QLI Areas</div>
                  {renderValueChips(qliAreas.value)}
                </div>
              ) : null}
              {showAreas ? (
                <div className="col-6">
                  <div className={styles.Label}>Areas</div>
                  {renderValueChips(areas.values)}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </Panel>
  );
}
